import React from 'react'

import { AlignItems, Box, Display } from '@gousto-internal/citrus-react'
import classnames from 'classnames'

import type { Recipe } from '@library/menu-service'
import type { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { RecipeAttributePill } from '../../RecipeAttributePill/RecipeAttributePill'
import {
  replaceDashWithUnderscore,
  formatDietaryText,
  formatShelfLifeText,
  formatProteinText,
} from './formatters'

import css from './RecipeAttributes.css'

type RecipeAttributesPillsProps = {
  fiveADay?: Recipe['fiveADay']
  shelfLife: Recipe['shelfLife']
  dietaryClaims?: Recipe['dietaryClaims']
  equipments?: MenuRecipe['equipment']
  proteinPerPortionMg?: number
  isHeatOnly?: boolean
  loading?: boolean
}

const LoadingSkeleton = ({ style }: { style?: React.CSSProperties }) => (
  <span className={css.loadingSkeleton} style={style} data-testid="LoadingSkeleton" />
)

export const RecipeAttributesPills = ({
  dietaryClaims,
  equipments,
  shelfLife,
  fiveADay,
  proteinPerPortionMg,
  isHeatOnly,
  loading,
}: RecipeAttributesPillsProps) => {
  const showProteinPill = isHeatOnly && proteinPerPortionMg

  if (loading) {
    return (
      <div className={classnames(css.attributes)}>
        {[...Array(4)].map(() => (
          <LoadingSkeleton key={Math.random()} style={{ height: '10px', marginBottom: '10px' }} />
        ))}
      </div>
    )
  }

  if (!dietaryClaims?.length && !shelfLife && !equipments?.length && !showProteinPill) {
    return null
  }

  return (
    <Box
      alignItems={AlignItems.FlexStart}
      wrap
      data-testid="RecipeAttributesPills"
      display={Display.Flex}
    >
      {dietaryClaims &&
        dietaryClaims?.length > 0 &&
        dietaryClaims?.map(({ slug, name }) => (
          <RecipeAttributePill
            key={slug}
            icon={replaceDashWithUnderscore(slug)}
            text={formatDietaryText(name)}
          />
        ))}

      {!!fiveADay && <RecipeAttributePill icon="5_a_day" text={`${fiveADay}/5 a day`} />}

      {shelfLife && (
        <RecipeAttributePill icon="calendar" text={formatShelfLifeText(shelfLife?.maxDays)} />
      )}

      {equipments &&
        equipments?.length > 0 &&
        equipments.map(({ name }) => (
          <RecipeAttributePill key={name} icon="equipment" text={name} />
        ))}

      {showProteinPill && (
        <RecipeAttributePill icon="arrow_up" text={formatProteinText(proteinPerPortionMg ?? 0)} />
      )}
    </Box>
  )
}
